"use client"

import { useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Book } from "@/schema"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/shad/tooltip"
import { cn } from "@ui/utils"
import { Check, ChevronDown, Ellipsis, Library, LibraryBig } from "lucide-react"

import { ALL_BOOKS_SQID, isAllBooksId } from "@/lib/all-books"
import { MAX_VISIBLE_BOOKS, orderedBooksForUser } from "@/lib/book-order"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { useLoadingRouter } from "@/hooks/use-loading-router"
import { BookIcon } from "@/components/books/book-icon"

function currentRouteForBook(pathname: string, bookId: string) {
  const segments = pathname.split("/")
  if (segments[2] === "audit-log" && isAllBooksId(bookId)) return `/${bookId}`
  if (segments[2] === "audit-log" && segments[3]) return `/${bookId}/audit-log`

  // ID_PATTERN: 10 alphanumeric chars, must contain at least one capital letter or number
  const ID_PATTERN = /^(?=[0-9a-zA-Z]{10}$)(?=.*[A-Z0-9])/
  const restOfPath = []
  for (let i = 2; i < segments.length; i++) {
    if (ID_PATTERN.test(segments[i])) break
    restOfPath.push(segments[i])
  }
  return `/${bookId}/${restOfPath.join("/")}`
}

function AllBooksHover({ isActive, href }: { isActive: boolean; href: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          href={href}
          className={cn(
            "text-sidebar-foreground relative flex aspect-square size-9 items-center justify-center rounded-lg",
            isActive
              ? "bg-sidebar-primary text-sidebar-primary-foreground"
              : "bg-sidebar-accent hover:bg-sidebar-accent-hover"
          )}
        >
          <Library className="size-5" strokeWidth={1.5} />
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" className="bg-sidebar-primary text-sidebar-primary-foreground">
        All books
      </TooltipContent>
    </Tooltip>
  )
}

interface BookHoverProps {
  book: Book
  isActive: boolean
  href: string
}

function BookHover({ book, isActive, href }: BookHoverProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          href={href}
          className={cn(
            "text-sidebar-foreground relative flex aspect-square size-9 items-center justify-center rounded-lg",
            isActive
              ? book.icon_image_url
                ? "bg-sidebar-accent after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-2 after:ring-slate-600 after:ring-inset"
                : "bg-sidebar-primary text-sidebar-primary-foreground"
              : "bg-sidebar-accent hover:bg-sidebar-accent-hover"
          )}
        >
          <BookIcon book={book} className="size-full rounded-lg" textClassName="text-sm font-medium" />
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" className="bg-sidebar-primary text-sidebar-primary-foreground">
        {book.name}
      </TooltipContent>
    </Tooltip>
  )
}

interface BookOverflowProps {
  books: Book[]
  currentBookId: string | undefined
  pathname: string
  activeOverflowBook?: Book
}

function BookOverflow({ books, currentBookId, pathname, activeOverflowBook }: BookOverflowProps) {
  const router = useLoadingRouter({ bookScoped: false })
  const [open, setOpen] = useState(false)

  const selectBook = (bookId: string) => {
    setOpen(false)
    router.push(currentRouteForBook(pathname, bookId))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={activeOverflowBook ? `${activeOverflowBook.name}, browse all books` : "Browse all books"}
              className="bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent-hover focus-visible:ring-sidebar-ring flex aspect-square size-9 items-center justify-center rounded-lg focus-visible:ring-2 focus-visible:outline-none"
            >
              {activeOverflowBook ? (
                <BookIcon book={activeOverflowBook} className="ring-sidebar-primary size-full rounded-lg ring-2" />
              ) : (
                <Ellipsis className="size-5" />
              )}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-sidebar-primary text-sidebar-primary-foreground">
          {activeOverflowBook ? `${activeOverflowBook.name} · Browse all books` : "Browse all books"}
        </TooltipContent>
      </Tooltip>
      <PopoverContent side="right" align="start" sideOffset={8} className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search books..." />
          <CommandList>
            <CommandEmpty>No books found</CommandEmpty>
            <CommandGroup>
              {books.map((book) => (
                <CommandItem key={book.id} value={`${book.name} ${book.id}`} onSelect={() => selectBook(book.id)}>
                  <BookIcon
                    book={book}
                    className="bg-sidebar-accent size-6 rounded-[3px]"
                    textClassName="text-[10px] font-medium"
                  />
                  <span className="min-w-0 flex-1 truncate">{book.name}</span>
                  {currentBookId === book.id && <Check className="text-icon" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function BookSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { data: user } = useFetchCurrentUser()
  const pathname = usePathname()
  const currentBookId = useBookId()

  if (!user) return null

  const isAllBooksActive = isAllBooksId(currentBookId)
  const hasMultipleBooks = user.books.length > 1
  const orderedBooks = orderedBooksForUser(user)
  const hasBookOverflow = orderedBooks.length > MAX_VISIBLE_BOOKS
  const firstBooks = orderedBooks.slice(0, MAX_VISIBLE_BOOKS)
  const currentBook = orderedBooks.find((book) => book.id === currentBookId)
  const visibleBooks =
    !user.sidebar_book_ids?.length &&
    hasBookOverflow &&
    currentBook &&
    !firstBooks.some((book) => book.id === currentBook.id)
      ? [...firstBooks.slice(0, MAX_VISIBLE_BOOKS - 1), currentBook]
      : firstBooks

  return (
    <div
      data-book-switcher
      className={cn(
        "bg-sidebar fixed inset-y-0 left-0 z-20 hidden h-screen max-h-screen flex-col items-center gap-6 overflow-hidden border-r transition-[width,transform,padding] duration-200 ease-linear min-[1440px]:flex",
        collapsed
          ? "min-[1440px]:w-0 min-[1440px]:-translate-x-full min-[1440px]:border-r-0 min-[1440px]:p-0"
          : "min-[1440px]:w-[53px] min-[1440px]:translate-x-0 min-[1440px]:p-2"
      )}
    >
      <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
        <LibraryBig className="text-sidebar-foreground size-6" strokeWidth={1.5} />
      </div>
      <div className="grow space-y-2">
        <TooltipProvider>
          {hasMultipleBooks && (
            <AllBooksHover isActive={isAllBooksActive} href={currentRouteForBook(pathname, ALL_BOOKS_SQID)} />
          )}
          {visibleBooks.map((book) => (
            <BookHover
              key={book.id}
              book={book}
              isActive={!isAllBooksActive && currentBookId === book.id}
              href={currentRouteForBook(pathname, book.id)}
            />
          ))}
          {hasBookOverflow && (
            <BookOverflow
              books={orderedBooks}
              currentBookId={currentBookId}
              pathname={pathname}
              activeOverflowBook={
                currentBook && !visibleBooks.some((book) => book.id === currentBook.id) ? currentBook : undefined
              }
            />
          )}
        </TooltipProvider>
      </div>
    </div>
  )
}

export function BookSwitcherMenuItems({
  open,
  onOpenChange,
  onSelect,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: () => void
}) {
  const { data: user } = useFetchCurrentUser()
  const pathname = usePathname()
  const currentBookId = useBookId()
  const router = useLoadingRouter({ bookScoped: false })
  if (!user || user.books.length < 2) return null
  const isAllBooksActive = isAllBooksId(currentBookId)
  const orderedBooks = orderedBooksForUser(user)
  const useBookDropdown = orderedBooks.length + 1 > 6
  const currentBook = orderedBooks.find((book) => book.id === currentBookId) ?? user.default_book ?? orderedBooks[0]
  const selectBook = (bookId: string) => {
    onSelect()
    router.push(currentRouteForBook(pathname, bookId))
  }
  return (
    <>
      <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">Book</DropdownMenuLabel>
      {!useBookDropdown && (
        <>
          <DropdownMenuItem onSelect={() => selectBook(ALL_BOOKS_SQID)}>
            <Library className="text-icon" />
            <span className="min-w-0 flex-1 truncate">All books</span>
            {isAllBooksActive && <Check className="text-icon" />}
          </DropdownMenuItem>
          {orderedBooks.map((book) => (
            <DropdownMenuItem key={book.id} onSelect={() => selectBook(book.id)}>
              <BookIcon
                book={book}
                className="bg-sidebar-accent size-6 rounded-[3px]"
                textClassName="text-[10px] font-medium"
              />
              <span className="min-w-0 flex-1 truncate">{book.name}</span>
              {!isAllBooksActive && currentBookId === book.id && <Check className="text-icon" />}
            </DropdownMenuItem>
          ))}
        </>
      )}
      {useBookDropdown && (
        <DropdownMenuItem
          aria-expanded={open}
          onSelect={(event) => {
            event.preventDefault()
            onOpenChange(!open)
          }}
        >
          {isAllBooksActive ? (
            <Library className="text-icon" />
          ) : (
            <BookIcon
              book={currentBook}
              className="bg-sidebar-accent size-6 rounded-[3px]"
              textClassName="text-[10px] font-medium"
            />
          )}
          <span className="min-w-0 flex-1 truncate">{isAllBooksActive ? "All books" : currentBook?.name}</span>
          <ChevronDown className={cn("text-icon", open && "rotate-180")} />
        </DropdownMenuItem>
      )}
      {useBookDropdown && open && (
        <Command
          onKeyDown={(event) => {
            if (event.key === "Tab" && !event.shiftKey) {
              const nextItem = event.currentTarget
                .closest('[role="menu"]')
                ?.querySelector<HTMLElement>('[role="menuitem"]:not([data-disabled])')
              if (nextItem) {
                event.preventDefault()
                nextItem.focus()
              }
            }
            if (event.key !== "Escape") event.stopPropagation()
          }}
        >
          <CommandInput
            aria-label="Search books"
            placeholder="Search books..."
            className="h-9"
            wrapperStyles="px-2"
            autoFocus
          />
          <CommandList className="max-h-64 border-t">
            <CommandEmpty>No books found</CommandEmpty>
            <CommandGroup>
              <CommandItem value="All books" onSelect={() => selectBook(ALL_BOOKS_SQID)}>
                <Library className="text-icon" />
                <span className="min-w-0 flex-1 truncate">All books</span>
                {isAllBooksActive && <Check className="text-icon" />}
              </CommandItem>
              {orderedBooks.map((book) => (
                <CommandItem key={book.id} value={`${book.name} ${book.id}`} onSelect={() => selectBook(book.id)}>
                  <BookIcon
                    book={book}
                    className="bg-sidebar-accent size-6 rounded-[3px]"
                    textClassName="text-[10px] font-medium"
                  />
                  <span className="min-w-0 flex-1 truncate">{book.name}</span>
                  {!isAllBooksActive && currentBookId === book.id && <Check className="text-icon" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      )}
      <DropdownMenuSeparator />
    </>
  )
}
